import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from "@angular/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import {
  NavigationError,
  provideRouter,
  withNavigationErrorHandler,
  withPreloading,
} from "@angular/router";

import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from "@angular/common/http";
import {
  provideClientHydration,
  withEventReplay,
} from "@angular/platform-browser";
import { routes } from "./app.routes";
import { authInterceptor } from "./core/interceptors/auth.interceptor";
import { wafEnvelopeInterceptor } from "./core/interceptors/waf-envelope.interceptor";
import { provideFeatureFlags } from "./core/feature-flags.provider";
import { provideSessionExpiryWatch } from "./core/session-expiry.provider";
import { PreloadAllWithStaleBuildRecovery } from "./core/stale-build-preloading";
import { recoverFromStaleBuild } from "./core/stale-build-reload";

export const appConfig: ApplicationConfig = {
  providers: [
    // wafEnvelopeInterceptor runs last so it sits closest to the network: every
    // other interceptor still sees the plain body, and the WAF envelope is the
    // final thing applied before the request leaves. It replaces the old
    // bootstrap gate that waited up to 3s for the Service Worker to take
    // control — encoding no longer depends on the worker existing, so there is
    // nothing to wait for.
    provideHttpClient(
      withFetch(),
      withInterceptors([authInterceptor, wafEnvelopeInterceptor]),
    ),
    // Mounted ui-app-config.json first, then the BFF's shared flag file while
    // signed in — see provideFeatureFlags / FeatureFlagService.
    provideFeatureFlags(),
    // "Session Expired" in an unattended tab — see SessionExpiryService.
    provideSessionExpiryWatch(),
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Preload every lazy chunk in the background after bootstrap so
    // RouterMenuService can see the full route tree (and thus the full
    // sidebar) shortly after the user lands. Acceptable cost for an
    // internal admin tool; revisit if the bundle ever starts blocking
    // first paint. PreloadAllWithStaleBuildRecovery is PreloadAllModules
    // plus a reload when a chunk has been deleted by a deploy — without
    // it those 404s silently cost the user whole menu sections.
    provideRouter(
      routes,
      withPreloading(PreloadAllWithStaleBuildRecovery),
      // A deploy replaces every content-hashed lazy chunk, so a tab opened
      // before it navigates into a 404 and stalls. Reload onto the target URL
      // to pick up the new build. Navigation-only: CanDeactivate has already
      // run by this point, so nothing the user meant to keep is discarded.
      withNavigationErrorHandler((event: NavigationError) =>
        recoverFromStaleBuild(event.error, event.url),
      ),
    ),
    provideClientHydration(withEventReplay()),
    provideAnimations(),
  ],
};
